import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { PreferenceType } from '@/types/food';
import { scheduleAllergenReminders } from '@/utils/notificationScheduler';
import { sendPartnerNotification, checkAndNotifyStreakDanger } from '@/utils/partnerNotifications';

export interface FoodLog {
  id: string;
  user_id: string;
  food_id: string;
  baby_id?: string | null;
  consumed_at: string;
  preference?: PreferenceType | null;
  allergy_reaction?: string | null;
  notes?: string | null;
  photo_url?: string | null;
  photo_storage_path?: string | null;
  photo_caption?: string | null;
  created_at?: string;
  updated_at?: string;
}

export function useFoodLogs() {
  const [foodLogs, setFoodLogs] = useState<FoodLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user, isPremium } = useAuth();
  const { isAdmin } = useAdmin();

  // Matlogg er en betalt funksjon. Serveren håndhever dette med en RESTRICTIVE
  // RLS-policy på food_logs (krever aktivt abonnement eller admin); her unngår
  // vi i tillegg å kalle databasen når brukeren ikke har tilgang.
  const hasTrackerAccess = !!user && (isPremium || isAdmin);

  // Fetch user's food logs with real-time updates
  useEffect(() => {
    if (!user || !hasTrackerAccess) {
      setFoodLogs([]);
      return;
    }


    const fetchFoodLogs = async () => {
      setIsLoading(true);
      try {
        // Databasereglene (RLS) returnerer egne logger + logger for barn
        // brukeren eier eller har fått delt via akseptert invitasjon.
        const { data, error } = await (supabase as any)
          .from('food_logs')
          .select('*')
          .order('consumed_at', { ascending: false });

        if (error) throw error;

        setFoodLogs((data as FoodLog[]) || []);
      } catch (error) {
        console.error('Error fetching food logs:', error);
        toast.error('Kunne ikke hente matlogger');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFoodLogs();

    // Sanntid: RLS filtrerer hendelser til rader brukeren har tilgang til,
    // slik at partnerens logger for delte barn også kommer inn.
    const subscription = (supabase as any)
      .channel(`food_logs_changes-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'food_logs',
        },
        (payload: any) => {
          if (payload.eventType === 'INSERT') {
            setFoodLogs(prev =>
              prev.some(l => l.id === payload.new.id) ? prev : [payload.new as FoodLog, ...prev]
            );
          } else if (payload.eventType === 'UPDATE') {
            setFoodLogs(prev => prev.map(log => 
              log.id === payload.new.id ? payload.new as FoodLog : log
            ));
          } else if (payload.eventType === 'DELETE') {
            setFoodLogs(prev => prev.filter(log => log.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, hasTrackerAccess]);

  const addFoodLog = async (logData: {
    food_id: string;
    baby_id?: string;
    consumed_at?: string;
    preference?: PreferenceType;
    allergy_reaction?: string;
    notes?: string;
    photo_url?: string;
    photo_storage_path?: string;
    photo_caption?: string;
  }) => {
    if (!user) {
      toast.error('Du må være innlogget for å logge mat');
      return;
    }

    if (!hasTrackerAccess) {
      toast.error('Matlogg er en Premium-funksjon');
      return;
    }

    // Optimistic insert — prepend a temporary log immediately
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimisticLog: FoodLog = {
      id: tempId,
      user_id: user.id,
      food_id: logData.food_id,
      baby_id: logData.baby_id ?? null,
      consumed_at: logData.consumed_at || new Date().toISOString(),
      preference: logData.preference ?? null,
      allergy_reaction: logData.allergy_reaction ?? null,
      notes: logData.notes ?? null,
      photo_url: logData.photo_url ?? null,
      photo_storage_path: logData.photo_storage_path ?? null,
      photo_caption: logData.photo_caption ?? null,
    };
    setFoodLogs(prev => [optimisticLog, ...prev]);

    try {
      // First, get the food details to check if it's an allergen
      const { data: foodData, error: foodError } = await (supabase as any)
        .from('foods')
        .select('name, allergens, allergy_risk')
        .eq('id', logData.food_id)
        .single();

      if (foodError) {
        console.error('Error fetching food details:', foodError);
      }

      // Check if this is the first time logging this food for this baby
      if (logData.baby_id && foodData?.allergens && foodData.allergens.length > 0) {
        const { data: existingLogs } = await (supabase as any)
          .from('food_logs')
          .select('id')
          .eq('food_id', logData.food_id)
          .eq('baby_id', logData.baby_id)
          .limit(1);

        // If this is the first exposure to an allergen, schedule reminders
        if (!existingLogs || existingLogs.length === 0) {
          const consumedDate = logData.consumed_at ? new Date(logData.consumed_at) : new Date();
          
          try {
            await scheduleAllergenReminders({
              userId: user.id,
              babyId: logData.baby_id,
              foodName: foodData.name,
              allergenType: foodData.allergens[0], // Use first allergen
              firstExposureDate: consumedDate,
            });
          } catch (reminderError) {
            console.error('Error scheduling allergen reminders:', reminderError);
            // Don't block the food log if reminder scheduling fails
          }
        }
      }

      const { data, error } = await (supabase as any)
        .from('food_logs')
        .insert({
          user_id: user.id,
          food_id: logData.food_id,
          baby_id: logData.baby_id || null,
          consumed_at: logData.consumed_at || new Date().toISOString(),
          preference: logData.preference || null,
          allergy_reaction: logData.allergy_reaction || null,
          notes: logData.notes || null,
          photo_url: logData.photo_url || null,
          photo_storage_path: logData.photo_storage_path || null,
          photo_caption: logData.photo_caption || null
        })
        .select()
        .single();

      if (error) throw error;

      // Replace optimistic log with real one
      setFoodLogs(prev => {
        const withoutTemp = prev.filter(l => l.id !== tempId);
        // Avoid duplicate if realtime already inserted it
        if (withoutTemp.some(l => l.id === (data as FoodLog).id)) return withoutTemp;
        return [data as FoodLog, ...withoutTemp];
      });
      
      // Send partner notification if baby is shared
      if (logData.baby_id && foodData?.name) {
        try {
          await sendPartnerNotification({
            babyId: logData.baby_id,
            type: 'food_log',
            title: '🍽️ Partner logget mat',
            body: `Partneren din logget "${foodData.name}" for barnet`,
            metadata: {
              foodName: foodData.name,
              preference: logData.preference,
            },
          });

          // Check if streak is still safe after this log
          const { data: baby } = await (supabase as any)
            .from('babies')
            .select('name')
            .eq('id', logData.baby_id)
            .single();

          if (baby?.name) {
            await checkAndNotifyStreakDanger(logData.baby_id, baby.name);
          }
        } catch (notifError) {
          console.error('Error sending partner notification:', notifError);
          // Don't block the log if notification fails
        }
      }
      
      // Success toast will be shown after streak/achievement checks
      return data;
    } catch (error) {
      console.error('Error adding food log:', error);
      // Rollback optimistic insert
      setFoodLogs(prev => prev.filter(l => l.id !== tempId));
      toast.error('Kunne ikke lagre matlogg');
    }
  };

  const updateFoodLog = async (id: string, updates: {
    consumed_at?: string;
    preference?: PreferenceType | null;
    allergy_reaction?: string | null;
    notes?: string | null;
  }) => {
    if (!user) return;
    if (!hasTrackerAccess) {
      toast.error('Matlogg er en Premium-funksjon');
      return;
    }

    try {
      const { data, error } = await (supabase as any)
        .from('food_logs')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setFoodLogs(prev => prev.map(log => log.id === id ? data as FoodLog : log));
      toast.success('Matlogg oppdatert');
    } catch (error) {
      console.error('Error updating food log:', error);
      toast.error('Kunne ikke oppdatere matlogg');
    }
  };

  const deleteFoodLog = async (id: string) => {
    if (!user) return;
    if (!hasTrackerAccess) {
      toast.error('Matlogg er en Premium-funksjon');
      return;
    }


    try {
      const { error } = await (supabase as any)
        .from('food_logs')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setFoodLogs(prev => prev.filter(log => log.id !== id));
      toast.success('Matlogg slettet');
    } catch (error) {
      console.error('Error deleting food log:', error);
      toast.error('Kunne ikke slette matlogg');
    }
  };

  // Get recent foods (last 30 days, max 10 unique foods)
  const getRecentFoods = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return foodLogs
      .filter(log => new Date(log.consumed_at) >= thirtyDaysAgo)
      .reduce((unique: FoodLog[], current: FoodLog) => {
        const found = unique.find(item => item.food_id === current.food_id);
        if (!found) {
          unique.push(current);
        }
        return unique;
      }, [])
      .slice(0, 10);
  };

  // Get orphan logs (logs without baby_id)
  const getOrphanLogs = () => {
    return foodLogs.filter(log => !log.baby_id);
  };

  // Repair orphan logs by assigning baby_id
  const repairOrphanLogs = async (updates: { logId: string; babyId: string }[]) => {
    if (!user) return;
    if (!hasTrackerAccess) {
      toast.error('Matlogg er en Premium-funksjon');
      return;
    }


    try {
      // Update all logs in parallel
      const updatePromises = updates.map(({ logId, babyId }) =>
        (supabase as any)
          .from('food_logs')
          .update({ baby_id: babyId })
          .eq('id', logId)
          .eq('user_id', user.id)
      );

      await Promise.all(updatePromises);
      
      toast.success(`${updates.length} ${updates.length === 1 ? 'logg' : 'logger'} reparert!`);
    } catch (error) {
      console.error('Error repairing orphan logs:', error);
      toast.error('Kunne ikke reparere logger');
    }
  };

  return {
    foodLogs,
    isLoading,
    addFoodLog,
    updateFoodLog,
    deleteFoodLog,
    getRecentFoods,
    getOrphanLogs,
    repairOrphanLogs
  };
}